export const GLOBAL_REVIEW_STRATEGY = "global_review";
export const FOCUSED_REVIEW_STRATEGY = "focused_review";
export const DOCUMENT_COMPARISON_STRATEGY = "document_comparison";
export const REFERENCE_REVIEW_STRATEGY = "reference_review";

export const isGlobalAnalysisCriterion = (criterion) => (
    String(criterion?.criterion_type || "").trim() === GLOBAL_REVIEW_STRATEGY
);

export const findGlobalAnalysisCriterion = (criteria = []) => (
    (criteria || []).find(isGlobalAnalysisCriterion) || null
);

export const isReferenceComparisonCriterion = (criterion) => {
    return String(criterion?.criterion_type || "").trim() === DOCUMENT_COMPARISON_STRATEGY;
};

export const isReferenceReviewCriterion = (criterion) => (
    String(criterion?.criterion_type || "").trim() === REFERENCE_REVIEW_STRATEGY
);

export const findReferenceComparisonCriterion = (criteria = []) => (
    (criteria || []).find(isReferenceComparisonCriterion) || null
);

export const isExclusiveSystemCriterion = (criterion) => (
    isGlobalAnalysisCriterion(criterion)
    || isReferenceComparisonCriterion(criterion)
    || isReferenceReviewCriterion(criterion)
);

const selectionId = (selection) => (
    selection && typeof selection === "object" ? selection.id : selection
);

const normalizeSelectionItem = (selection) => {
    if (!selection || typeof selection !== "object") {
        return selection ? { id: selection, ref: [] } : null;
    }

    return {
        id: selection.id,
        ref: Array.isArray(selection.ref) ? selection.ref : [],
    };
};

export const normalizeGlobalAnalysisSelection = (
    criteria = [],
    selections = [],
    { preferGlobal = false } = {}
) => {
    const normalized = (selections || [])
        .map(normalizeSelectionItem)
        .filter((selection) => selection?.id);

    const globalCriterion = findGlobalAnalysisCriterion(criteria);
    const globalId = globalCriterion?.id;
    if (!globalId) return normalized;

    const hasGlobal = normalized.some((selection) => selectionId(selection) === globalId);
    if (!hasGlobal || normalized.length <= 1) return normalized;

    if (preferGlobal) {
        const globalSelection = normalized.find((selection) => selectionId(selection) === globalId);
        return globalSelection ? [globalSelection] : [];
    }

    return normalized.filter((selection) => selectionId(selection) !== globalId);
};

export const normalizeExclusiveSystemSelection = (
    criteria = [],
    selections = [],
    { preferExclusiveId = null, preferGlobal = false } = {}
) => {
    const normalized = normalizeGlobalAnalysisSelection(criteria, selections, { preferGlobal });
    if (normalized.length <= 1) return normalized;

    const criteriaById = new Map((criteria || []).map((criterion) => [criterion?.id, criterion]));
    const referenceComparisonSelections = normalized.filter((selection) => (
        isReferenceComparisonCriterion(criteriaById.get(selectionId(selection)))
        || isReferenceReviewCriterion(criteriaById.get(selectionId(selection)))
    ));
    if (referenceComparisonSelections.length > 0) {
        const preferredReferenceSelection = referenceComparisonSelections.find(
            (selection) => selectionId(selection) === preferExclusiveId
        );
        return [preferredReferenceSelection || referenceComparisonSelections[0]];
    }

    const exclusiveSelections = normalized.filter((selection) => (
        isExclusiveSystemCriterion(criteriaById.get(selectionId(selection)))
    ));

    if (exclusiveSelections.length <= 0) return normalized;

    const preferredExclusiveSelection = exclusiveSelections.find(
        (selection) => selectionId(selection) === preferExclusiveId
    );
    if (preferredExclusiveSelection) return [preferredExclusiveSelection];

    if (exclusiveSelections.length === normalized.length) {
        return [exclusiveSelections[exclusiveSelections.length - 1]];
    }

    return normalized.filter((selection) => (
        !isExclusiveSystemCriterion(criteriaById.get(selectionId(selection)))
    ));
};
